import { cn } from '@/lib/utils'
import { formatCurrency, formatMinutes } from '@/lib/format'
import { CREDIT_TYPE_LABELS } from '../constants'
import type { BillingCredit, CreditEntryType } from '../types'
import { HeaderCell } from './HeaderCell'

/** The ledger puts a credit in the month of its UTC timestamp, so the date
 *  shown is the UTC day: in the Americas, a credit from the first hours of
 *  the 1st would otherwise read as the last day of the month before. */
const UTC_DAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

const TYPE_BADGE_CLASSES: Record<CreditEntryType, string> = {
  credit_purchase: 'bg-primary-subtle-bg text-primary-low-contrast',
  credit_compensation: 'bg-green-50 text-green-800',
}

export function CreditsTable({ credits }: { credits: BillingCredit[] }) {
  const totalMinutes = credits.reduce((sum, c) => sum + Number(c.minutes), 0)
  const totalAmount = credits.reduce((sum, c) => sum + Number(c.amount), 0)
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-[10px] text-muted-foreground border-b border-border">
          <HeaderCell align="left">Date</HeaderCell>
          <HeaderCell align="left">Type</HeaderCell>
          <HeaderCell align="left">Invoice</HeaderCell>
          <HeaderCell align="right">Minutes</HeaderCell>
          <HeaderCell align="right">Amount ($)</HeaderCell>
        </tr>
      </thead>
      <tbody>
        {credits.map((credit) => (
          <CreditRow key={credit.id} credit={credit} />
        ))}
        <tr>
          <td className="pt-3 font-semibold">Total</td>
          <td className="pt-3" />
          <td className="pt-3" />
          <td className="pt-3 text-right font-semibold">
            {`+${formatMinutes(totalMinutes)}`}
          </td>
          <td className="pt-3 text-right font-bold">
            {formatCurrency(Math.abs(totalAmount))}
          </td>
        </tr>
      </tbody>
    </table>
  )
}

function CreditRow({ credit }: { credit: BillingCredit }) {
  return (
    <tr className="border-b border-border">
      <td className="py-2.5">{UTC_DAY.format(new Date(credit.created_at))}</td>
      <td className="py-2.5">
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
            TYPE_BADGE_CLASSES[credit.entry_type]
          )}>
          {CREDIT_TYPE_LABELS[credit.entry_type]}
        </span>
      </td>
      <td className="py-2.5">
        {credit.invoice_numbers.length > 0 ? (
          credit.invoice_numbers.join(', ')
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-2.5 text-right">
        {`+${formatMinutes(Number(credit.minutes))}`}
      </td>
      <td className="py-2.5 text-right">
        {/* The ledger stores a credit's USD as zero or negative. */}
        {formatCurrency(Math.abs(Number(credit.amount)))}
      </td>
    </tr>
  )
}
